import { useContext, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import axios from 'axios'
import flatten from 'lodash/flatten'
import get from 'lodash/get'

import { CURRENT_USER_AND_PROJECT_QUERY } from './queries'
import { RootContext } from '../root/RootContext'

const NITROGEN_BASE_URL = process.env.REACT_APP_API_ROOT
const PROFILE_ACTION = '/aep/profile'
const OFFER_ACTION = '/aep/offers'
const EXPERIENCE_EVENTS_ACTION = '/aep/experience-events'
const GET_SEGMENT_DEFINITION = '/aep/segments'

const segmentMembershipPath = 'entity.segmentMembership.ups'

const headers = window.location.href.includes('localhost')
  ? {
      'x-access-token': process.env.REACT_APP_DEV_TOKEN,
      'Access-Control-Allow-Origin': '*',
    }
  : {}

const nitrogen = axios.create({
  baseURL: NITROGEN_BASE_URL,
  headers: headers,
})

export const useCurrentUserAndProject = (params) => {
  const { loading, error, data } = useQuery(CURRENT_USER_AND_PROJECT_QUERY, params)
  return {
    loading,
    error,
    data,
  }
}

export const useOffer = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchData = async (params) => {
    try {
      setLoading(true)

      const offerResponse = await nitrogen.get(OFFER_ACTION, {
        params,
      })

      const propositions = offerResponse.data.result.map((a) => {
        return a.offers.map((o) => {
          o.propositionId = a.propositionID
          delete o.offerName //while waiting for the right offername to show
          return o
        })
      })
      const propositionRequestsArray = params?.propositionRequests ? JSON.parse(params?.propositionRequests) : []
      const pp = flatten(propositions)

      let offers = []
      propositionRequestsArray?.length > 0 &&
        propositionRequestsArray
          .map((element) => {
            pp.map((p) => {
              if (element['xdm:activityId'] == p['xdm:activity']['xdm:id']) {
                if (p.hasOwnProperty(['xdm:options'])) {
                  //personalized offers exists
                  p['xdm:options'].map((oc, idx) => {
                    let r = {}
                    try {
                      r = JSON.parse(oc['xdm:content'])
                    } catch (e) {
                      r.content = oc['xdm:content']
                    }
                    r.offerId = oc['xdm:id']
                    r.id = pp[0]['propositionId']
                    r.activityId = pp[0]['xdm:activity']['xdm:id']
                    offers.push(r)
                    //return r
                  })
                  //offers.push(offerContent)
                  //return offerContent
                } else if (p.hasOwnProperty(['xdm:fallback'])) {
                  //it is a fallback offer
                  let r = {}
                  try {
                    r = JSON.parse(p['xdm:fallback']['xdm:content'])
                  } catch (e) {
                    r.content = p['xdm:fallback']['xdm:content']
                  }
                  r.offerId = p['xdm:fallback']['xdm:id']
                  r.id = pp[0]['propositionId']
                  r.activityId = pp[0]['xdm:activity']['xdm:id']
                  offers.push(r)
                  //return r
                } else {
                  //we shouldn't end up here
                  const errorMsg = `[useOffer] unable to parse response for ${JSON.stringify(p)}`
                  throw new Error(errorMsg)
                }
              }
            })
          })
          .flat()
      setData(offers)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }
  return [
    {
      data,
      loading,
      error,
    },
    fetchData,
  ]
}

export function useProfile(params) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchData = async () => {
    try {
      setLoading(true)

      const profileResponse = await nitrogen.get(PROFILE_ACTION, {
        params,
      })

      const profile = Object.values(profileResponse.data.result)[0]
      if (!profile.entityId) {
        setError(true)
      }
      const experienceEventsResponse = await nitrogen.get(EXPERIENCE_EVENTS_ACTION, {
        params: {
          ...params,
        },
      })

      const segmentDefinitionResponse = await nitrogen.get(GET_SEGMENT_DEFINITION, {
        params: {
          ...params,
        },
      })

      const segmentsList = segmentDefinitionResponse.data.result
        ? Object.entries(segmentDefinitionResponse.data.result.segments)
        : ''

      const getsegmentFieldValue = (path) => {
        let resolvedSegment = Object.entries(get(profile, path, '--')).filter(function (segment) {
          return ['existing', 'realized'].includes(segment?.[1].status?.toString())
        })

        var segmentDetails =
          segmentsList && get(profile, path)
            ? segmentsList.filter(function (segment) {
                return resolvedSegment.some(function (segmentId) {
                  return segment[1].id === segmentId[0]
                    ? (segment[1]['lastQualificationTime'] = segmentId[1].lastQualificationTime)
                    : ''
                })
              })
            : ''
        return segmentDetails
      }
      const segmentDetails = getsegmentFieldValue(segmentMembershipPath) || ''

      setData({
        profile: profile,
        experienceEvents: experienceEventsResponse.data?.result.children,
        segmentsList: segmentDetails,
        searchParams: params,
      })
    } catch (err) {
      console.error('useProfile Error: ', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }
  return [
    {
      data,
      loading,
      error,
    },
    fetchData,
  ]
}

export function useAep() {
  const { project } = useContext(RootContext)
  const [data, setData] = useState()

  const aep = project?.integrations?.aep
  const sandbox = aep?.sandbox
  const imsOrg = aep?.imsOrg

  const streamingEndpoint = data?.streamingEndpoint
  const details = data?.details
  const datasets = data?.datasets

  useEffect(() => {
    if (sandbox && imsOrg?.id) {
      nitrogen(`/aep/${imsOrg.id}/${sandbox}`, { params: { include: 'streamingEndpoint,details,datasets' } }).then(
        (response) => setData(response?.data?.result)
      )
    }
  }, [])

  const sendEvent = async (payload) => {
    try {
      if (!streamingEndpoint) throw new Error('Streaming endpoint is missing.')

      const options = {
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
        method: 'post',
        data: payload,
      }
      const response = await axios(streamingEndpoint, options)
      return response
    } catch (err) {
      console.error(err)
    }
  }

  return { sandbox, imsOrg, streamingEndpoint, details, datasets, sendEvent }
}
